import { useEffect, useRef } from 'react';

/* =============================================================================
   usePageCanvasColor — html/body follow the portal's page surface (ADDITIVE)
   -----------------------------------------------------------------------------
   THE BUG THIS FIXES (portal task "Portal dom background in dark mode when
   scrolling reveals white bg"): .portal-shell paints `background:
   var(--surface-page)` and is `min-height: 100vh`, so the shell itself themes
   correctly. But `data-theme` lives on the shell — a DIV — and custom properties
   inherit DOWNWARD only. html and body are its ANCESTORS, so they can never see
   the theme's --surface-page. App.css then paints

       body, html { background: var(--gradient-page) !important }

   from a :root LIGHT token, which is what shows in the overscroll / rubber-band
   region behind the shell on a dark theme.

   WHY IMPERATIVE, AND NOT A STYLESHEET RULE. The CSS fix would be to mirror
   `data-theme` onto <html> and add an `html[data-theme=...]` rule. That works,
   but it widens what the attribute selects: comfort-portal.css documents (and
   depends on) the attribute living ONLY on the portal wrapper, so every
   `[data-theme] .btn-cyv-teal`-style rule in that file would begin matching
   document-wide. Reading the token off the shell and painting imperatively
   changes nothing about what any selector matches.

   THE COLOUR IS READ FROM COMPUTED STYLE, never looked up in a table — the same
   decision, for the same reason, as the sibling useChromeThemeColor: the shell
   carries both `data-theme` and the token set, so getComputedStyle resolves the
   value the shell is actually painted with and cannot drift from
   PortalLayout.css / tokens/colors.css. Adding a fourth theme stays a token
   change with no edit here.

   !important is required on the write because the App.css declaration being
   corrected is itself !important, and an ordinary inline style loses to it.

   BOTH html AND body are painted. html alone fixes the canvas (the overscroll
   region), but body carries the same !important gradient over its own box, and
   `background-attachment: fixed` makes that visible wherever the opaque shell
   does not cover it. Painting both removes the whole class rather than the one
   reported symptom.

   The rAF-deferred cleanup is deliberate and is the same self-heal the sibling
   hook documents: on a portal->portal swap between the two shells (PortalLayout
   <-> PortalSpaceChrome) React can run this cleanup AFTER the incoming shell's
   effect, which would strand the canvas on the public-site default. Re-reading
   the DOM on the next frame is correct whichever order they run in.
   ============================================================================= */

// The page surface the shell is actually painted with, or '' if unreadable.
// getPropertyValue returns a leading space — trim is mandatory.
function readCanvasColor(el) {
	if (!el) return '';
	return getComputedStyle(el).getPropertyValue('--surface-page').trim();
}

function paint(color) {
	if (typeof document === 'undefined') return;
	const targets = [document.documentElement, document.body];
	targets.forEach((el) => {
		if (!el) return;
		if (color) {
			el.style.setProperty('background', color, 'important');
			el.style.setProperty('background-attachment', 'initial', 'important');
		} else {
			// Restore App.css's own declaration by removing ours entirely, rather
			// than writing the light value back — the public site owns that value.
			el.style.removeProperty('background');
			el.style.removeProperty('background-attachment');
		}
	});
}

/**
 * @param {React.RefObject<HTMLElement>} shellRef ref to the .portal-shell element
 * @param {string|null} appliedTheme the resolved data-theme value (null = default)
 */
export function usePageCanvasColor(shellRef, appliedTheme) {
	// Holds any pending restore scheduled by a previous cleanup, so the next
	// effect run can cancel it. A cleanup cannot cancel its own frame.
	const pendingRestore = useRef(0);

	useEffect(() => {
		if (typeof document === 'undefined') return undefined;

		// A restore queued by the outgoing shell is now stale — this shell is
		// mounted and authoritative.
		if (pendingRestore.current) {
			cancelAnimationFrame(pendingRestore.current);
			pendingRestore.current = 0;
		}

		// 'default' (no attribute) must stay byte-identical to the untouched
		// portal, so it paints nothing and leaves App.css alone.
		paint(appliedTheme ? readCanvasColor(shellRef.current) : '');

		return () => {
			pendingRestore.current = requestAnimationFrame(() => {
				pendingRestore.current = 0;
				const shell = document.querySelector('.portal-shell[data-theme]');
				paint(shell ? readCanvasColor(shell) : '');
			});
		};
	}, [shellRef, appliedTheme]);
}

export default usePageCanvasColor;
